import { Box, Chip } from "@mui/material";
import { ChartLine, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";

const ANNUAL_SAVINGS_TAG = /^([$€£]\s*\d[\d,.]*(?:[km])?)\s*\/\s*(?:year|yr)$/i;
const DURATION_TAG =
  /^\d+(?:[.,]\d+)?\s*(?:seconds?|secs?|minutes?|mins?|hours?|hrs?|days?|weeks?|[smhdw])$/i;

export function TemplateTags({
  tags = [],
  maxTags = Infinity,
  compact = false,
  savingsOnly = false,
  includeTimeSavings = false,
}) {
  const { t } = useTranslation();
  const visibleTags = (tags ?? [])
    .filter((tag) => {
      const title = tag.trim();
      if (ANNUAL_SAVINGS_TAG.test(title)) return true;
      if (DURATION_TAG.test(title)) return includeTimeSavings;
      return !savingsOnly;
    })
    .slice(0, maxTags);

  if (visibleTags.length === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        minWidth: 0,
        flexWrap: compact ? "nowrap" : "wrap",
      }}
    >
      {visibleTags.map((tag) => {
        const savings = tag.trim().match(ANNUAL_SAVINGS_TAG);
        const timeSavings = includeTimeSavings && DURATION_TAG.test(tag.trim());
        const isSavings = Boolean(savings || timeSavings);
        const label = savings
          ? t("flows.templateSavings", { amount: savings[1] })
          : timeSavings
            ? t("flows.templateTimeSavings", { duration: tag.trim() })
            : tag;

        return (
          <Chip
            key={tag}
            size="small"
            variant={isSavings ? "filled" : "outlined"}
            color={timeSavings ? "info" : "default"}
            label={label}
            title={label}
            icon={
              savings ? (
                <ChartLine size={16} aria-hidden="true" />
              ) : timeSavings ? (
                <Clock size={16} aria-hidden="true" />
              ) : undefined
            }
            sx={{
              height: 28,
              minWidth: 0,
              maxWidth: "100%",
              flexShrink: compact ? 1 : 0,
              ...(isSavings && {
                borderRadius: 999,
                fontWeight: 600,
                "& .MuiChip-icon": { color: "inherit", flexShrink: 0 },
              }),
              ...(savings && {
                backgroundColor: "var(--success-100)",
                color: "var(--success-300)",
              }),
            }}
          />
        );
      })}
    </Box>
  );
}
